import React, { createContext, useContext, useState, useEffect } from "react";

/**
 * @typedef {"self" | "organization" | "sub"} TenantType
 */

/**
 * @typedef {Object} AuthType
 * @property {string} type
 * @property {boolean} tenant
 * @property {TenantType} tenant_type
 */

/**
 * @typedef {Object} TokenType
 * @property {string} access_token
 * @property {string} refresh_token
 */

/**
 * @typedef {Object} UserType
 * @property {AuthType} auth
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {string|null} username
 * @property {number} role_id
 * @property {string} role_slug
 * @property {number|null} school_id
 * @property {string|null} tenant_id
 * @property {string} locale
 * @property {boolean} can_change_locale
 */

/**
 * @typedef {Object} TenantAuthContextType
 * @property {UserType|null|undefined} currentUser - The authenticated user (undefined during loading, null when logged out, UserType when logged in)
 * @property {React.Dispatch<React.SetStateAction<UserType|null|undefined>>} setCurrentUser - Function to update user state
 * @property {boolean} loading - Loading state indicator
 * @property {(user: UserType, token: TokenType) => void} login - Function to log in a user
 * @property {() => void} logout - Function to log out the current user
 */

const TenantAuthContext = createContext(
  /** @type {TenantAuthContextType|undefined} */(undefined),
);

const userStorageKey = "tenant-user";
const tokenStorageKey = "tenant-token";

export const TenantAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(
    /** @type {UserType|null|undefined} */(undefined),
  );
  const [loading, setLoading] = useState(true);

  /**
   * @param {UserType} user
   * @param {TokenType} token
   */
  const persistUser = (user, token) => {
    localStorage.setItem(userStorageKey, JSON.stringify(user));
    localStorage.setItem(tokenStorageKey, JSON.stringify(token));
  };

  /**
   * Function to clear user and token from localStorage
   */
  const clearStorage = () => {
    localStorage.removeItem(userStorageKey);
    localStorage.removeItem(tokenStorageKey);
  };

  /**
   *Function to set the the token
   *@param {TokenType} token
   */
  const persistToken = (token) => {
    localStorage.setItem(tokenStorageKey, JSON.stringify(token));
  };

  /**
   * @description Function to get the access token from localStorage
   * @returns {string|null}
   */
  const getAccessToken = () => {
    const token = localStorage.getItem(tokenStorageKey);
    if (!token) return null;
    /** @type {TokenType} */
    const parsedToken = JSON.parse(token);
    return parsedToken.access_token;
  };

  /**
   * @description Function to get the refresh token from localStorage
   * @returns {string|null}
   */
  const getRefreshToken = () => {
    const token = localStorage.getItem(tokenStorageKey);
    if (!token) return null;
    /** @type {TokenType} */
    const parsedToken = JSON.parse(token);
    return parsedToken.refresh_token;
  };

  /**
   * @param {UserType} user
   * @param {TokenType} token
   */
  const login = (user, token) => {
    setCurrentUser(user);
    persistUser(user, token);
  };

  const logout = async () => {
    setCurrentUser(null);
    clearStorage();
  };

  useEffect(() => {
    const storedUser = localStorage.getItem(userStorageKey);

    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
      setLoading(false);
    } else {
      setLoading(false);
      setCurrentUser(null);
    }
  }, []);

  return (
    <TenantAuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        loading,
        login,
        logout,
        getAccessToken,
        getRefreshToken,
        persistToken,
      }}
    >
      {children}
    </TenantAuthContext.Provider>
  );
};

/**
 * Custom hook to access Tenant Auth Context
 * @returns {TenantAuthContextType} The tenant auth context value
 */
export const useTenantAuth = () => {
  const context = useContext(TenantAuthContext);
  if (!context) {
    throw new Error("useTenantAuth must be used within an TenantAuthProvider");
  }
  return context;
};
